import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { useParams } from "react-router-dom";

import { useCurrentPermissions } from "../../contexts/PermissionsProvider";
import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import {
  DetailShell,
  ScopeBadge,
  SectionCard,
  SkeletonTable,
} from "../../components/Directory/primitives";
import {
  addEntitlement,
  getRole,
  removeEntitlement,
  revokeRoleAssignment,
} from "./api";

/**
 * DirectoryRoleDetail — /directory/roles/:id.
 *
 * Two-tab detail surface (Entitlements / Assigned to) sitting on the
 * shared DetailShell + SectionCard primitives so the hero + section
 * shells match UserDetail exactly.
 */

// Loose entitlement validation: verb.noun tokens separated by dots.
// Rejects whitespace and reserved characters coord would refuse.
const ENT_RE = /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/;

export default function DirectoryRoleDetail() {
  const { id } = useParams();
  const { permissions } = useCurrentPermissions();
  const isSystem =
    (permissions || []).includes("SYSTEM") ||
    (permissions || []).includes("SYSTEM_DIRECTORY_ADMIN");

  const [role, setRole] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);
  const [addEntOpen, setAddEntOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    getRole(id)
      .then(setRole)
      .catch((e) =>
        setErr(
          e.status === 403
            ? "You don't have permission to view this role."
            : e.status === 404
              ? "Role not found."
              : `Failed to load role: ${e.message}`
        )
      );
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const predefined =
    role &&
    (role.predefined === true ||
      (role.predefined === undefined &&
        (role.accountId === null || role.accountId === undefined)));

  const scopeAccountId = role?.accountId || null;

  const revoke = async (subject) => {
    try {
      if (scopeAccountId) {
        await revokeRoleAssignment({
          accountId: scopeAccountId,
          subjectType: subject.type,
          subjectId: subject.id,
          roleId: id,
        });
      } else if (subject.type === "GROUP") {
        await revokeRoleAssignment({ groupId: subject.id, roleId: id });
      } else {
        setErr("SYSTEM role→user revoke requires an account scope.");
        return;
      }
      setToast({ message: "Assignment revoked." });
      load();
    } catch (e) {
      setErr(`Failed to revoke. HTTP ${e.status || "?"} — try again in a moment.`);
    }
  };

  const loading = !role && !err;

  return (
    <DetailShell
      backTo="/directory/roles"
      parentLabel="Roles"
      parentTo="/directory/roles"
      title={role?.name || (loading ? "Loading…" : "Role")}
      badges={
        role
          ? [
              predefined ? (
                <Tooltip
                  key="p"
                  title="System-managed — cannot be renamed or deleted"
                >
                  <Chip
                    icon={<LockOutlinedIcon fontSize="small" />}
                    label="Predefined"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              ) : null,
              role.accountId ? (
                <ScopeBadge
                  key="s"
                  scope="account"
                  name={role.accountName || role.accountId}
                />
              ) : (
                <ScopeBadge key="s" scope="system" />
              ),
            ]
          : []
      }
      description={role?.description || (loading ? undefined : "No description.")}
    >
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {loading && (
        <SkeletonTable
          columns={[{ label: "Field" }, { label: "Value" }]}
          rows={4}
        />
      )}

      {role && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Entitlements" />
              <Tab label="Assigned to" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <EntitlementsPanel
              role={role}
              canEdit={isSystem && !predefined}
              lockedReason={
                predefined
                  ? "System-managed — entitlements are read-only."
                  : !isSystem
                    ? "SYSTEM privileges required to edit entitlements."
                    : null
              }
              addOpen={addEntOpen}
              setAddOpen={setAddEntOpen}
              onChanged={(msg) => {
                setToast({ message: msg });
                load();
              }}
              onErr={setErr}
            />
          )}

          {tab === 1 && (
            <AssignedToPanel
              role={role}
              onRevoke={revoke}
              onAssign={() => setAssignOpen(true)}
            />
          )}

          <AssignmentPickerDialog
            open={assignOpen}
            mode="assign_role"
            roleId={id}
            roleName={role.name}
            scopeAccountId={scopeAccountId}
            onClose={() => setAssignOpen(false)}
            onSaved={({ count }) => {
              setAssignOpen(false);
              setToast({ message: `Assigned to ${count} subject(s).` });
              load();
            }}
          />
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity="success"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </DetailShell>
  );
}

function EntitlementsPanel({
  role,
  canEdit,
  lockedReason,
  addOpen,
  setAddOpen,
  onChanged,
  onErr,
}) {
  const items = Array.isArray(role.entitlements) ? role.entitlements : [];

  const submitRemove = async (ent) => {
    try {
      await removeEntitlement({ rid: role.id, entitlement: ent });
      onChanged("Entitlement removed.");
    } catch (e) {
      onErr(
        `Failed to remove entitlement. HTTP ${e.status || "?"} — try again in a moment.`
      );
    }
  };

  const action = canEdit ? (
    <Tooltip
      title="Add a permission key (verb.noun) to this role. Every current and future holder gets it."
      arrow
      enterDelay={400}
    >
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setAddOpen(true)}
        sx={{ textTransform: "none" }}
        aria-label="Add entitlement"
      >
        Add entitlement
      </Button>
    </Tooltip>
  ) : lockedReason ? (
    <Tooltip title={lockedReason}>
      <span>
        <Button
          size="small"
          startIcon={<LockOutlinedIcon fontSize="small" />}
          disabled
          sx={{ textTransform: "none" }}
        >
          Locked
        </Button>
      </span>
    </Tooltip>
  ) : null;

  return (
    <>
      <SectionCard
        title="Entitlements"
        count={items.length}
        action={action}
        isEmpty={items.length === 0}
        emptyState={{
          title: "No entitlements yet",
          description: canEdit
            ? "Add verb tokens (e.g. apps.write) so this role actually authorises something."
            : "This role has no entitlements. Ask a SYSTEM operator to add them.",
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {items.map((ent) => (
            <Chip
              key={ent}
              label={ent}
              size="small"
              variant="outlined"
              onDelete={canEdit ? () => submitRemove(ent) : undefined}
              deleteIcon={
                canEdit ? (
                  <DeleteOutlineIcon aria-label={`Remove ${ent}`} />
                ) : undefined
              }
              sx={{ fontFamily: "monospace" }}
            />
          ))}
        </Stack>
      </SectionCard>

      <AddEntitlementDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={async (ent) => {
          try {
            await addEntitlement({ rid: role.id, entitlement: ent });
            setAddOpen(false);
            onChanged("Entitlement added.");
          } catch (e) {
            throw new Error(
              `Failed to add. HTTP ${e.status || "?"} — try again in a moment.`
            );
          }
        }}
      />
    </>
  );
}

function AddEntitlementDialog({ open, onClose, onAdd }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValue("");
    setErr(null);
    setBusy(false);
  }, [open]);

  const trimmed = value.trim();
  const valid = ENT_RE.test(trimmed);
  const helper = !trimmed
    ? "Format: verb.noun (e.g. apps.write, projects.read)."
    : valid
      ? " "
      : "Use lowercase verb.noun tokens separated by dots.";

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await onAdd(trimmed);
    } catch (e) {
      setErr(e.message || "Failed to add.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add entitlement</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Entitlement"
            placeholder="apps.write"
            fullWidth
            autoFocus
            value={value}
            error={!!(trimmed && !valid)}
            helperText={err || helper}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{ "aria-label": "Entitlement verb" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !valid}
        >
          {busy ? "Adding…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignedToPanel({ role, onRevoke, onAssign }) {
  const users = Array.isArray(role.assignedUsers) ? role.assignedUsers : [];
  const groups = Array.isArray(role.assignedGroups) ? role.assignedGroups : [];
  const total = users.length + groups.length;

  return (
    <SectionCard
      title="Assigned to"
      count={total}
      action={
        <Tooltip
          title="Grant this role to a user or group. Choose scope: platform, account, or project."
          arrow
          enterDelay={400}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAssign}
            sx={{ textTransform: "none" }}
            aria-label="Assign to…"
          >
            Assign to…
          </Button>
        </Tooltip>
      }
      isEmpty={total === 0}
      emptyState={{
        title: "Not assigned yet",
        description:
          "Use Assign to… above to grant this role to one or more users or groups. Group assignments cascade to every member.",
      }}
    >
      <Stack spacing={1.5}>
        <SubjectGroup
          icon={<PeopleOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />}
          label="Users"
          count={users.length}
        >
          {users.map((u) => (
            <SubjectRow
              key={u.id}
              primary={u.name || u.email || u.id}
              secondary={u.email || u.id}
              onRevoke={() => onRevoke({ type: "USER", id: u.id })}
              revokeLabel={`Revoke ${u.name || u.email || "user"}`}
            />
          ))}
        </SubjectGroup>

        {users.length > 0 && groups.length > 0 && (
          <Divider flexItem />
        )}

        <SubjectGroup
          icon={<WorkspacesOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />}
          label="Groups"
          count={groups.length}
        >
          {groups.map((g) => (
            <SubjectRow
              key={g.id}
              primary={g.name || g.id}
              secondary={g.accountName || g.id}
              onRevoke={() => onRevoke({ type: "GROUP", id: g.id })}
              revokeLabel={`Revoke ${g.name || "group"}`}
            />
          ))}
        </SubjectGroup>
      </Stack>
    </SectionCard>
  );
}

function SubjectGroup({ icon, label, count, children }) {
  if (!count) return null;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="overline" color="text.secondary">
          {label} ({count})
        </Typography>
      </Stack>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  );
}

function SubjectRow({ primary, secondary, onRevoke, revokeLabel }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        "& .row-actions": {
          opacity: 0,
          transition: "opacity 150ms ease-in-out",
        },
        "&:hover, &:focus-within": {
          bgcolor: "action.hover",
          "& .row-actions": { opacity: 1 },
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {primary}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {secondary}
        </Typography>
      </Box>
      <Box className="row-actions">
        <Tooltip title="Revoke assignment">
          <IconButton
            size="small"
            color="error"
            onClick={onRevoke}
            aria-label={revokeLabel}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
}
